"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { RefreshCw } from "lucide-react";
import { useAuthedApi } from "../../api/useAuthedApi";
import type { DiscoveredTool, McpScope } from "../../api/customMcpApi";
import {
  categorize,
  groupTools,
  matchesQuery,
  OTHER,
  sectionLabels,
  serverGroupsTools,
  TAG_QUERY_PREFIX,
} from "./toolCategorize";
import { ToolCategorySection } from "./ToolCategorySection";

type StatusFilter = "all" | "enabled" | "disabled";
type KindFilter = "all" | "read" | "write";

interface Props {
  open: boolean;
  scope: McpScope;
  /** Connector name (identity), or null when closed. */
  name: string | null;
  displayName?: string;
  onClose: () => void;
  /** Called after a successful save so the list refreshes its counts. */
  onSaved: () => void;
}

/** Edit which discovered tools a custom MCP exposes. Loads the CACHED discovery
 *  result (no secret, no live probe); "Refresh from server" re-probes on demand.
 *  Save persists the curated allowlist via the same skills endpoint the wizard
 *  uses. */
export function ManageToolsDialog({
  open,
  scope,
  name,
  displayName,
  onClose,
  onSaved,
}: Props) {
  const { customMcp } = useAuthedApi();
  const [tools, setTools] = useState<DiscoveredTool[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [everDiscovered, setEverDiscovered] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [layoutVersion, setLayoutVersion] = useState(0);

  const load = useCallback(async () => {
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      const r = await customMcp.getTools(scope, name);
      setTools(r.discovered);
      setLayoutVersion((v) => v + 1);
      setSelected(new Set(r.curated));
      setEverDiscovered(r.discovered_at_least_once);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load tools");
    } finally {
      setLoading(false);
    }
  }, [customMcp, scope, name]);

  useEffect(() => {
    if (open && name) void load();
    if (!open) {
      setTools([]);
      setSelected(new Set());
      setError(null);
      setQuery("");
      setStatusFilter("all");
      setKindFilter("all");
    }
  }, [open, name, load]);

  const refresh = async () => {
    if (!name) return;
    setRefreshing(true);
    setError(null);
    try {
      const r = await customMcp.discover(scope, name);
      if (r.needs_auth) {
        setError("This connector needs to be re-authorized before tools can be listed.");
      } else if (!r.server_up) {
        setError("Couldn't reach the MCP server. Showing the last cached tools.");
      } else {
        setTools(r.tools);
        setLayoutVersion((v) => v + 1);
        // Keep prior selection; drop any that no longer exist upstream.
        const names = new Set(r.tools.map((t) => t.upstream_name));
        setSelected((prev) => new Set([...prev].filter((n) => names.has(n))));
        setEverDiscovered(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const toggle = (tool: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tool)) next.delete(tool);
      else next.add(tool);
      return next;
    });
  };

  const setMany = (names: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const n of names) {
        if (on) next.add(n);
        else next.delete(n);
      }
      return next;
    });
  };

  // A server that groups none of its tools gets one section named after the
  // connector; one that groups some puts the rest under "Other". Decided over
  // every tool so sections don't shift while searching or filtering.
  const ungrouped = useMemo(
    () => (serverGroupsTools(tools) ? OTHER : displayName?.trim() || name || OTHER),
    [tools, displayName, name],
  );

  const labels = useMemo(() => sectionLabels(tools), [tools]);

  const visible = useMemo(
    () =>
      tools.filter((t) => {
        if (!matchesQuery(t, query)) return false;
        const on = selected.has(t.upstream_name);
        if (statusFilter === "enabled" && !on) return false;
        if (statusFilter === "disabled" && on) return false;
        if (kindFilter !== "all" && categorize(t).kind !== kindFilter) return false;
        return true;
      }),
    [tools, query, statusFilter, kindFilter, selected],
  );

  // Header counts are over the whole category, not the filtered view.
  const totals = useMemo(() => {
    const m = new Map<string, { total: number; enabled: number }>();
    for (const t of tools) {
      const { category } = categorize(t, ungrouped, labels);
      const c = m.get(category) ?? { total: 0, enabled: 0 };
      c.total += 1;
      if (selected.has(t.upstream_name)) c.enabled += 1;
      m.set(category, c);
    }
    return m;
  }, [tools, selected, ungrouped, labels]);

  const groups = useMemo(() => groupTools(visible, ungrouped, labels), [visible, ungrouped, labels]);
  const narrowed = query.trim() !== "" || statusFilter !== "all" || kindFilter !== "all";

  const save = async () => {
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      await customMcp.saveSkills(scope, name, Array.from(selected));
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Manage tools{displayName ? ` — ${displayName}` : ""}</span>
        <Button
          size="small"
          startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
          onClick={refresh}
          disabled={refreshing || loading || saving}
        >
          Refresh from server
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : tools.length === 0 ? (
          <Stack spacing={2} sx={{ py: 2, alignItems: "flex-start" }}>
            <Typography variant="body2" color="text.secondary">
              {everDiscovered
                ? "No tools were discovered. Try refreshing from the server."
                : "This connector hasn't been discovered yet. Refresh from the server to list its tools."}
            </Typography>
          </Stack>
        ) : (
          <>
            <Stack spacing={1.5} sx={{ mb: 1 }}>
              <TextField
                size="small"
                label="Search tools"
                placeholder="Name, description or tag:billing"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={statusFilter}
                  onChange={(_, v: StatusFilter | null) => v && setStatusFilter(v)}
                  aria-label="Status filter"
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="enabled">Enabled</ToggleButton>
                  <ToggleButton value="disabled">Disabled</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={kindFilter}
                  onChange={(_, v: KindFilter | null) => v && setKindFilter(v)}
                  aria-label="Type filter"
                >
                  <ToggleButton value="all">All types</ToggleButton>
                  <ToggleButton value="read">Read</ToggleButton>
                  <ToggleButton value="write">Write</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {selected.size} of {tools.length} exposed
                </Typography>
                <Box>
                  <Button size="small" onClick={() => setMany(visible.map((t) => t.upstream_name), true)}>
                    {narrowed ? "Select shown" : "Select all"}
                  </Button>
                  <Button
                    size="small"
                    // Unfiltered "Clear all" empties the selection outright, so a saved
                    // tool that has since vanished upstream is cleared too.
                    onClick={() =>
                      narrowed ? setMany(visible.map((t) => t.upstream_name), false) : setSelected(new Set())
                    }>
                    {narrowed ? "Clear shown" : "Clear all"}
                  </Button>
                </Box>
              </Box>
            </Stack>
            {groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No tools match.
              </Typography>
            ) : (
              groups.map((g) => {
                const c = totals.get(g.category) ?? { total: g.tools.length, enabled: 0 };
                return (
                  <ToolCategorySection
                    // A new tool list (load / refresh) remounts the sections so
                    // each re-derives its default open state.
                    key={`${layoutVersion}:${g.category}`}
                    category={g.category}
                    tools={g.tools}
                    enabledCount={c.enabled}
                    total={c.total}
                    selected={selected}
                    onTagClick={(tag) => setQuery(`${TAG_QUERY_PREFIX}${tag}`)}
                    onToggle={toggle}
                    onSetMany={setMany}
                    defaultExpanded={c.enabled > 0 || totals.size === 1}
                    forceExpanded={narrowed}
                  />
                );
              })
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loading || tools.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save {selected.size} exposed
        </Button>
      </DialogActions>
    </Dialog>
  );
}
